// Página de resultados da busca (/search) e página 404 no DS. Estas peças são
// as mesmas no portal e na intranet; o Search.jsx de cada site só cuida do
// estado (na intranet, os resultados ficam numa chave por usuário).
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';

export const TAMANHO = 20;

// Abas por tipo de conteúdo. Imagem não entra em nenhuma: arquivo de imagem
// solto ("saúde-indígena.png") não é resultado de busca para quem lê o site.
// "Tudo" é a soma exata das outras três.
export const ABAS = [
  { id: 'paginas', rotulo: 'Páginas', tipos: ['Document', 'Folder', 'Link'] },
  { id: 'noticias', rotulo: 'Notícias', tipos: ['News Item', 'Event'] },
  { id: 'documentos', rotulo: 'Documentos', tipos: ['File'] },
];
export const TUDO = {
  id: 'tudo',
  rotulo: 'Tudo',
  tipos: ABAS.flatMap((aba) => aba.tipos),
};
export const TODAS_AS_ABAS = [TUDO, ...ABAS];

export const ORDENS = [
  { id: 'relevancia', rotulo: 'Relevância' },
  {
    id: 'recentes',
    rotulo: 'Mais recentes',
    sort_on: 'effective',
    sort_order: 'descending',
  },
  {
    id: 'az',
    rotulo: 'A–Z',
    sort_on: 'sortable_title',
    sort_order: 'ascending',
  },
];

// Fora da busca: a pasta que guarda a configuração do site (as colunas do
// rodapé editável). As páginas dela são públicas porque o rodapé as lê, mas
// não são conteúdo. O catálogo não tem "exceto este caminho", então a busca vai
// restrita ao que existe na raiz do site, menos estes caminhos.
export const FORA_DA_BUSCA = ['/configuracao'];

// Listagem da raiz que dá os caminhos da busca.
export const OPCOES_DA_RAIZ = { 'path.depth': 1, b_size: 200 };

const pelaId = (lista, id, padrao) =>
  lista.find((item) => item.id === id) || padrao;

// Aceita a parte de busca do endereço ("?SearchableText=…") ou ele inteiro.
export const lerUrl = (search = '') => {
  const texto = String(search);
  const p = new URLSearchParams(texto.slice(texto.indexOf('?') + 1));
  const pagina = parseInt(p.get('pagina'), 10);
  return {
    termo: (p.get('SearchableText') || '').trim(),
    // Etiqueta da notícia ("Assuntos"): /search?Subject=Saúde%20Indígena
    assunto: (p.get('Subject') || '').trim(),
    aba: pelaId(TODAS_AS_ABAS, p.get('tipo'), TUDO).id,
    ordem: pelaId(ORDENS, p.get('ordem'), ORDENS[0]).id,
    pagina: pagina > 0 ? pagina : 1,
  };
};

// Endereço da página de resultados; o que é padrão fica fora da URL.
export const montarUrl = ({ termo, assunto, aba, ordem, pagina }) => {
  const p = new URLSearchParams();
  if (termo) p.set('SearchableText', termo);
  if (assunto) p.set('Subject', assunto);
  if (aba && aba !== TUDO.id) p.set('tipo', aba);
  if (ordem && ordem !== ORDENS[0].id) p.set('ordem', ordem);
  if (pagina > 1) p.set('pagina', String(pagina));
  const texto = p.toString();
  return `/search${texto ? `?${texto}` : ''}`;
};

const caminhoDe = (item) => flattenToAppURL(item?.['@id'] || '');

const foraDaBusca = (caminho) =>
  FORA_DA_BUSCA.some((f) => caminho === f || caminho.startsWith(`${f}/`));

// Caminhos dos itens da raiz que entram na busca.
export const caminhosDaRaiz = (itens = []) =>
  itens
    .map(caminhoDe)
    .filter((c) => c.startsWith('/') && c !== '/' && !foraDaBusca(c));

// Quando a busca sai do servidor do frontend, ela vai direto ao backend
// (RAZZLE_INTERNAL_API_PATH, ex. http://127.0.0.1:8080/Plone), e ali o catálogo
// quer o caminho físico (/Plone/transparencia). Pelo endereço público (++api++)
// o caminho é relativo à raiz do site (/transparencia).
export const prefixoDoSite = () => {
  const servidor = typeof __SERVER__ !== 'undefined' && __SERVER__;
  const interno = servidor ? config.settings.internalApiPath : null;
  if (!interno) return '';
  try {
    return new URL(interno).pathname.replace(/\/+$/, '');
  } catch {
    return '';
  }
};

// A ação de busca do core monta o endereço sem codificar os valores: termo com
// acento ou tipo com espaço ("News Item") iam crus, e o backend recusava a
// busca feita pelo servidor (400).
const base = ({
  termo,
  assunto,
  aba,
  caminhos = [],
  prefixo = prefixoDoSite(),
}) => ({
  SearchableText: termo ? encodeURIComponent(termo) : undefined,
  ...(assunto ? { Subject: [encodeURIComponent(assunto)] } : {}),
  portal_type: pelaId(TODAS_AS_ABAS, aba, TUDO).tipos.map(encodeURIComponent),
  ...(caminhos.length
    ? { 'path.query': caminhos.map((c) => encodeURI(`${prefixo}${c}`)) }
    : {}),
});

export const opcoesDaBusca = ({
  termo,
  assunto,
  aba,
  ordem,
  pagina = 1,
  caminhos,
  prefixo,
}) => {
  const o = pelaId(ORDENS, ordem, ORDENS[0]);
  return {
    ...base({ termo, assunto, aba, caminhos, prefixo }),
    ...(o.sort_on ? { sort_on: o.sort_on, sort_order: o.sort_order } : {}),
    b_size: TAMANHO,
    b_start: (pagina - 1) * TAMANHO,
    metadata_fields: ['effective', 'mime_type'],
  };
};

// A contagem de cada aba só depende do termo, do assunto e dos caminhos.
export const opcoesDaContagem = ({
  termo,
  assunto,
  aba,
  caminhos,
  prefixo,
}) => ({
  ...base({ termo, assunto, aba, caminhos, prefixo }),
  b_size: 1,
});

const ENTIDADES = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

// Resumo vindo do WordPress às vezes traz entidade HTML ("400&#215;400"). Vira
// texto aqui; quem escapa na tela é o React.
export const decodificar = (texto = '') =>
  String(texto).replace(
    /&(#\d+|#x[0-9a-f]+|amp|lt|gt|quot|apos|nbsp);/gi,
    (todo, e) => {
      if (e[0] !== '#') return ENTIDADES[e.toLowerCase()] ?? todo;
      const n =
        e[1] === 'x' || e[1] === 'X'
          ? parseInt(e.slice(2), 16)
          : parseInt(e.slice(1), 10);
      return n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : todo;
    },
  );

const ROTULO_DO_TIPO = {
  Document: 'Página',
  Folder: 'Página',
  Link: 'Link',
  'News Item': 'Notícia',
  Event: 'Evento',
  File: 'Documento',
};

const FORMATOS = [
  ['pdf', 'PDF'],
  ['wordprocessingml', 'DOCX'],
  ['msword', 'DOC'],
  ['spreadsheetml', 'XLSX'],
  ['ms-excel', 'XLS'],
  ['presentationml', 'PPTX'],
  ['ms-powerpoint', 'PPT'],
  ['opendocument.text', 'ODT'],
  ['opendocument.spreadsheet', 'ODS'],
  ['zip', 'ZIP'],
  ['csv', 'CSV'],
];

export const formatoDoArquivo = (mime) => {
  if (!mime) return null;
  const achado = FORMATOS.find(([trecho]) => mime.includes(trecho));
  if (achado) return achado[1];
  const sub = mime.split('/')[1] || '';
  return /^[a-z0-9]{2,5}$/i.test(sub) ? sub.toUpperCase() : null;
};

// Data da parte literal do ISO (evita deslocamento de fuso). Arquivo migrado
// sem data de publicação vem como 1969.
export const dataDoItem = (iso) => {
  if (!iso || iso.startsWith('1969') || iso.startsWith('1970-01-01'))
    return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
};

// Seções do menu (sem o Início e sem link externo), para os atalhos da 404 e
// para dizer em que seção cada resultado está.
export const secoesDoMenu = (itens = []) =>
  itens
    .map((item) => ({
      caminho: caminhoDe(item),
      titulo: item.title,
      descricao: item.description || '',
    }))
    .filter((s) => s.caminho.startsWith('/') && s.caminho !== '/' && s.titulo);

export const metaDoItem = (item, secoes = []) => {
  const caminho = caminhoDe(item);
  const secao = secoes.find(
    (s) => caminho === s.caminho || caminho.startsWith(`${s.caminho}/`),
  );
  const detalhe =
    item['@type'] === 'File'
      ? formatoDoArquivo(item.mime_type)
      : dataDoItem(item.effective);
  return [
    ROTULO_DO_TIPO[item['@type']] || item['@type'],
    detalhe,
    secao?.titulo,
  ]
    .filter(Boolean)
    .join(' · ');
};

export const numero = (n) => Number(n || 0).toLocaleString('pt-BR');
